import { Head, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface AdminUser {
  id: number
  name: string
  email: string
  avatar_url: string | null
  is_superadmin: boolean
  account_name: string | null
  role: string | null
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  search: string | null
  sort: string
  direction: string
  users: AdminUser[]
  pagination: PaginationMeta
}

const COLUMNS = [
  { key: "name", label: "User", sortable: true },
  { key: "account_name", label: "Account", sortable: false },
  { key: "role", label: "Role", sortable: false },
  { key: "created_at", label: "Joined", sortable: true },
  { key: "actions", label: "", sortable: false },
] as const

export default function AdminUsersIndex({ search, sort, direction, users, pagination }: Props) {
  const [searchValue, setSearchValue] = useState(search || "")
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)

  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      if (search) p.search = search
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      const filtered = Object.entries(overrides).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== "")
      return { ...p, ...Object.fromEntries(filtered) }
    },
    [search, sort, direction],
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/users", buildParams({ search: searchValue || undefined, page: undefined }), { preserveState: true })
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get("/admin/users", buildParams({ sort: column, direction: newDirection, page: undefined }), { preserveState: true })
  }

  return (
    <AdminLayout>
      <Head title="Users | Admin" />
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold text-cream">Users</h1>
            <p className="mt-1 text-sm text-cream/50">
              {pagination.count} registered {pagination.count === 1 ? "user" : "users"}
            </p>
          </div>

          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-cream/30" />
              <Input
                placeholder="Search name or email..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-8 w-56 border-white/10 bg-white/5 pl-8 text-sm text-cream placeholder:text-cream/30"
              />
            </div>
            {search && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-cream/50 hover:text-cream"
                onClick={() => {
                  setSearchValue("")
                  router.get("/admin/users", {}, { preserveState: true })
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                {COLUMNS.map((col) => (
                  <th key={col.key} className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">
                    {col.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-cream/70 transition-colors"
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                        <SortIcon active={sort === col.key} direction={sort === col.key ? direction : undefined} />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-cream/30">
                    {search ? `No users matching "${search}"` : "No users found"}
                  </td>
                </tr>
              )}
              {users.map((user) => {
                const initials = user.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)

                return (
                  <tr key={user.id} className="border-b border-white/[0.04] text-cream/70">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="size-8">
                          <AvatarImage src={user.avatar_url || undefined} />
                          <AvatarFallback className="bg-white/10 text-xs text-cream/60">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 font-medium text-cream">
                            <span className="truncate">{user.name}</span>
                            {user.is_superadmin && (
                              <Badge className="bg-red/10 text-red border-red/20 text-[10px] shrink-0">
                                superadmin
                              </Badge>
                            )}
                          </p>
                          <p className="truncate text-xs text-cream/40">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-cream/50">{user.account_name || "\u2014"}</td>
                    <td className="whitespace-nowrap px-4 py-3 capitalize text-cream/50">{user.role || "\u2014"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-cream/40">
                      {new Date(user.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="xs"
                          variant="ghost"
                          className="text-cream/40 hover:text-cream"
                          onClick={() => router.post(`/admin/users/${user.id}/impersonate`)}
                        >
                          Impersonate
                        </Button>
                        {!user.is_superadmin && (
                          <Button
                            size="xs"
                            variant="ghost"
                            className="text-red/40 hover:text-red"
                            onClick={() => setDeleteTarget(user)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          baseUrl="/admin/users"
          params={buildParams()}
          pagination={pagination}
        />
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?
              This will remove the user and all their created listings and videos. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleteTarget) {
                  router.delete(`/admin/users/${deleteTarget.id}`)
                  setDeleteTarget(null)
                }
              }}
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}
